import { useQuery } from "@tanstack/react-query";
import { legacyAdminGet } from "../utils/PrecargaApi";
import { useAuth } from "./useAuth";

/**
 * Custom hook for managing Precarga Wallets
 * @param {object} options - Query options
 * @param {number} options.eventId - Precarga event ID
 * @param {number} options.page - Page number (default 1)
 * @param {number} options.pageSize - Page size (default 20)
 * @param {string} options.status - Filter by status: LINKED, NOT_LINKED
 * @param {string} options.search - Search by user email or phone
 * @param {string} options.sortBy - Sort by: created_at, balance_account, balance_card
 * @param {string} options.sortDir - Sort direction: asc, desc
 */
export function usePrecargaWallets({
  eventId,
  page = 1,
  pageSize = 20,
  status = "",
  search = "",
  sortBy = "created_at",
  sortDir = "desc",
} = {}) {
  const { user } = useAuth();
  const token = user?.token;

  // List wallets
  const listQuery = useQuery({
    queryKey: ["precargaWallets", { eventId, page, pageSize, status, search, sortBy, sortDir }],
    queryFn: async () => {
      const params = { page, pageSize, sortBy, sortDir };
      if (status) params.status = status;
      if (search) params.search = search;

      return await legacyAdminGet(`${eventId}/wallets`, params);
    },
    enabled: !!token && !!eventId,
    keepPreviousData: true,
  });

  // Get single wallet
  const getWallet = async (walletId) => {
    if (!eventId || !walletId) return null;
    return await legacyAdminGet(`${eventId}/wallets/${walletId}`);
  };

  // Get wallet detail
  const getWalletDetail = async (walletId) => {
    if (!eventId || !walletId) return null;
    return await legacyAdminGet(`${eventId}/wallets/${walletId}/detail`);
  };

  return {
    wallets: listQuery.data?.items || [],
    totalCount: listQuery.data?.totalCount || 0,
    hasMore: listQuery.data?.hasMore || false,
    isLoading: listQuery.isLoading,
    isError: listQuery.isError,
    error: listQuery.error,
    refetch: listQuery.refetch,
    getWallet,
    getWalletDetail,
  };
}

export default usePrecargaWallets;
